import { useAuth0 } from '@auth0/auth0-react';
import Cookies from 'js-cookie';
import { AcuityContext } from 'models/Application';
import { useEffect, useRef, useState } from 'react';
import { setAccessToken, setAcuityContext, setUser } from 'redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

// auth0-spa-js raises these when a silent token request needs the user to sign in interactively - for
// example when Safari's "Prevent cross-site tracking" blocks the hidden auth0.com iframe it relies on.
// A blocked iframe can also surface as 'timeout' (auth0-spa-js gives up on it after 60 seconds).
const INTERACTIVE_LOGIN_ERRORS = ['login_required', 'consent_required', 'interaction_required', 'timeout'];

// At most one automatic trip to the login page per window *that has not yet succeeded*. If signing in
// still fails straight after a trip to the login page, stop redirecting and ask the user to sign in
// instead of bouncing forever. A successful sign-in clears the marker, because in Safari every fresh
// page load needs a trip to the login page and a quick reload must not look like a loop. The timestamp
// lives in sessionStorage because each trip to the login page is a full page load.
const LOGIN_REDIRECT_KEY = 'sparing_requests_login_redirect_at';
const LOGIN_REDIRECT_WINDOW_MS = 30 * 1000;
const SIGN_IN_FAILED_MESSAGE = 'We could not sign you in automatically.';

const needsInteractiveLogin = (error: unknown): boolean => INTERACTIVE_LOGIN_ERRORS.includes((error as { error?: string } | undefined)?.error ?? '');

const readLastLoginRedirect = (): number => {
  try {
    return Number(sessionStorage.getItem(LOGIN_REDIRECT_KEY) ?? 0);
  } catch (error) {
    return 0;
  }
};

const recordLoginRedirect = (): void => {
  try {
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(Date.now()));
  } catch (error) {
    // Storage unavailable (e.g. older Safari private browsing) - the redirect still happens.
  }
};

const clearLoginRedirect = (): void => {
  try {
    sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
  } catch (error) {
    // Nothing to clear if storage is unavailable.
  }
};

const readAcuityContextCookie = (): AcuityContext | undefined => {
  const cookie = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`);

  try {
    return cookie ? JSON.parse(cookie) : undefined;
  } catch (error) {
    return undefined;
  }
};

export const useAuthentication = (): { isAppLoading: boolean; cookieNotFound: boolean; authError: string | null; signIn: () => void } => {
  const { accessToken } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const { isAuthenticated, loginWithRedirect, isLoading, user, getAccessTokenSilently } = useAuth0();
  const [cookieNotFound, setCookieNotFound] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const hasRequestedConfig = useRef(false);
  // Two token requests race on startup; once one of them has sent the user to the login page, the
  // other's failure must not also raise the sign-in prompt on the way out.
  const isRedirectingToLogin = useRef(false);
  const isAppLoading = Boolean(isLoading || isConfigLoading);

  // A sign-in the user asked for; always allowed.
  const signIn = (): void => {
    isRedirectingToLogin.current = true;
    recordLoginRedirect();
    loginWithRedirect({ appState: { targetUrl: '/' } });
  };

  // A sign-in the app decided on; rate-limited so a login that keeps failing cannot loop.
  const redirectToLogin = (): void => {
    if (isRedirectingToLogin.current) return;

    if (Date.now() - readLastLoginRedirect() < LOGIN_REDIRECT_WINDOW_MS) {
      setAuthError(SIGN_IN_FAILED_MESSAGE);

      return;
    }

    signIn();
  };

  // Check if User is logged in, if not, redirect them to login screen
  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      redirectToLogin();

      return;
    }

    if (accessToken === null) {
      getAccessTokenSilently()
        .then((token) => {
          // Signed in: the last trip to the login page worked, so the next one (e.g. after a reload in
          // Safari, where the token only lives in memory) is a fresh sign-in, not a loop.
          clearLoginRedirect();
          dispatch(setAccessToken(token));
        })
        .catch((error) => (needsInteractiveLogin(error) ? redirectToLogin() : setAuthError(SIGN_IN_FAILED_MESSAGE)));
    }
    // redirectToLogin is rebuilt every render but only reads sessionStorage and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, dispatch, getAccessTokenSilently, isAuthenticated, isLoading]);

  useEffect(() => {
    if (user) {
      dispatch(setUser(user));
    }
  }, [dispatch, user]);

  // Fetch cookie AND configuration and set in store - but only once Auth0 has finished loading and has a
  // signed-in user. This used to run the moment the app mounted, asking for a token before the login
  // callback had finished. Chrome covered that with the auth0.com iframe; Safari blocks the iframe, so
  // the call threw outside the try/catch and left the app on its loader.
  useEffect(() => {
    if (isLoading || !isAuthenticated || hasRequestedConfig.current) return;

    hasRequestedConfig.current = true;

    const cookieJSON = readAcuityContextCookie();

    if (!cookieJSON?.selectedCustomer) {
      setCookieNotFound(true);

      return;
    }

    const fetchConfig = async (): Promise<void> => {
      setIsConfigLoading(true);

      try {
        const token = await getAccessTokenSilently();
        const resp = await fetch(`${process.env.REACT_APP_CALVIN_BASE_URL}/divisions/${cookieJSON.selectedCustomer.id}/sparingRequestConfiguration`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-functions-key': `${process.env.REACT_APP_API_HOST_KEY_CALVIN}`,
            authorization: `Bearer ${token}`,
            'Access-Control-Allow-Origin': '*'
          }
        });

        const respJSON = await resp.json();

        dispatch(setAcuityContext({ ...cookieJSON, configuration: respJSON }));
      } catch (error) {
        if (needsInteractiveLogin(error)) redirectToLogin();
      } finally {
        setIsConfigLoading(false);
      }
    };

    fetchConfig();
    // Runs once per page load, as soon as Auth0 reports a signed-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  // No Acuity division selected: send the user back to the Acuity dashboard to pick one. Only reachable
  // after a successful sign-in, so a failed login can no longer bounce the user to the dashboard.
  useEffect(() => {
    if (!cookieNotFound) return undefined;

    const timer = setTimeout(() => {
      window.location.href = process.env.REACT_APP_ERNIE_REDIRECT_URL || '';
    }, 5000);

    return (): void => clearTimeout(timer);
  }, [cookieNotFound]);

  return { isAppLoading, cookieNotFound, authError, signIn };
};
